import clsx from "clsx";
import { FC, useState } from "react";

import type { ModelEvent } from "@tsmono/inspect-common/types";
import { useEventListener } from "@tsmono/react/hooks";
import { Icon } from "@tsmono/react/icons";
import { formatTime } from "@tsmono/util";

import { attemptDurationSec } from "./attemptDuration";
import styles from "./RetryChip.module.css";
import { summarizeModelError } from "./summarizeModelError";

interface RetryChipProps {
  attempts: ModelEvent[];
  selectedKey: string;
  onSelect: (key: string) => void;
  keyOf: (event: ModelEvent) => string;
}

export const RetryChip: FC<RetryChipProps> = ({
  attempts,
  selectedKey,
  onSelect,
  keyOf,
}) => {
  const [open, setOpen] = useState(false);

  useEventListener(open ? window : null, "keydown", (e) => {
    if (e.key === "Escape") setOpen(false);
  });

  if (attempts.length <= 1) return null;

  const priorCount = attempts.length - 1;
  const selectedIndex = attempts.findIndex((a) => keyOf(a) === selectedKey);
  const isCurrentSelected =
    selectedIndex === -1 || selectedIndex === attempts.length - 1;

  const chipText = isCurrentSelected
    ? priorCount === 1
      ? "1 prior retry"
      : `${priorCount} prior retries`
    : `attempt ${selectedIndex + 1} · ${summarizeModelError(attempts[selectedIndex]?.error)}`;

  const handleSelect = (key: string) => {
    setOpen(false);
    onSelect(key);
  };

  return (
    <span className={styles.wrap}>
      <button
        type="button"
        className={clsx(styles.chip, open && styles.chipOpen)}
        title={`${chipText} — click to view`}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name="retry" className={styles.chipIcon} />
        <span className={styles.chipText}>{chipText}</span>
        <Icon name="chevronDown" className={styles.chipChevron} />
      </button>
      {open && (
        <>
          {/* Mouse-only dismissal; Escape closes the menu. */}
          <div
            className={styles.backdrop}
            role="presentation"
            onClick={() => setOpen(false)}
          />
          <div className={styles.menu}>
            {attempts.map((attempt, idx) => {
              const key = keyOf(attempt);
              const isCurrent = idx === attempts.length - 1;
              const label = isCurrent
                ? `Attempt ${idx + 1} · current`
                : `Attempt ${idx + 1} · ${summarizeModelError(attempt.error)}`;
              const duration = formatAttemptDuration(attempt);
              return (
                <button
                  key={key}
                  type="button"
                  className={clsx(
                    styles.item,
                    selectedKey === key && styles.itemActive
                  )}
                  onClick={() => handleSelect(key)}
                >
                  <span className={styles.itemNum}>{idx + 1}</span>
                  <span className={styles.itemLabel}>{label}</span>
                  {duration && (
                    <span className={styles.itemStatus}>{duration}</span>
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </span>
  );
};

function formatAttemptDuration(event: ModelEvent): string | null {
  const sec = attemptDurationSec(event);
  return sec != null ? formatTime(sec) : null;
}
